import { useGetQueryData } from "@/utils/react-query-hooks/general";
import { cloneDeep } from "lodash";
import React, { createContext, useContext, useRef, useState } from "react";

export const AnnoContextLex = createContext();

export const AnnoContextProviderLex = ({children, value}) => {
  const userData = useGetQueryData('userData');

  /**
   * note that we have a listener in OnChangePlugin
   * which listens for any changes in annoData, and 
   * triggers off the parentOnChange function to send
   * updated data to parent component
   */
  const [annoData, setAnnoData] = useState(value?.annoData);
  /**
   * need this, to access latest annoData inside of MarkNode mutation listener in AnnoPlugin.
   * Why? cuz this latest annoData helps us figure out whether to paint an anno yellow OR
   * blue or pink (cuz of toggle tag types)
   * 
   * Hacky yes. but a lot of this anno stuff is quite hacky..
   */
  const latestAnnoDataRef = useRef(value?.annoData)

  /**
   * Synchronous "annotation mutation in flight" signal. While true,
   * OnChangePlugin's Path 1 (LexicalOnChangePlugin's synchronous onChange)
   * skips its fire — preventing a partial-state save during the commit
   * window of an annotation create / edit / remove.
   *
   * Set to true at the START of any annotation flow that commits via
   * editor.update; auto-cleared by setTimeout(0). We use setTimeout (a
   * macrotask) rather than queueMicrotask because Lexical itself commits
   * editor.update via a microtask — if our reset is queued as a microtask
   * too, it can run *before* Lexical's commit microtask, leaving the ref
   * cleared by the time Path 1 fires. setTimeout(0) is a macrotask, which
   * runs after all pending microtasks (including Lexical's commit and the
   * resulting listener fires), so the ref reliably stays true through the
   * entire commit window.
   */
  const isAnnoMutatingRef = useRef(false)

/**
 * @description
 *
 * type can be 'tags' for now. in the future it could
 * be 'comments' and such. This separation will help
 * in the future
 *
 * @param isAnnoMutation - pass true when this setAnnoData call is part of a
 * larger annotation mutation (e.g. an annotation create where the lexical
 * mark wrap is queued in the same editor.update). This flips
 * isAnnoMutatingRef and queues a microtask reset, suppressing
 * OnChangePlugin's Path 1 during the commit window.
 */
const handleSetAnnoData = ({type = 'tags', dataId, data, isAnnoMutation, createdVia: createdViaParam}) => {
  if (isAnnoMutation === true) {
    isAnnoMutatingRef.current = true;
    // setTimeout(0) (macrotask) rather than queueMicrotask, because Lexical
    // commits editor.update via its own microtask — a microtask reset would
    // fire BEFORE Lexical's commit microtask, leaving the ref cleared by
    // the time Path 1 reads it.
    setTimeout(() => {
      isAnnoMutatingRef.current = false;
    }, 0);
  }
  setAnnoData(prev => {
    const existingEntry = prev ? (prev[dataId] || {}) : {};
    const now = new Date();
    const toReturn = ({
      ...(prev || {}),
      [dataId]: {
        ...existingEntry,
        author: existingEntry.author || { id: userData?.user._id, name: userData?.user.name },
        createdVia: existingEntry.createdVia || createdViaParam || 'human',
        createdAt: existingEntry.createdAt || now,
        updatedAt: now,
        [type]: data
      }
    })
    latestAnnoDataRef.current = toReturn;
    return toReturn
  }
  )
}


/**
 * marknode obj looks like:
 * {
 *   <annoDataId> : <fragment_as_serialized_editorState>,
 *   ...
 * }
 * 
 * annoData looks like: 
 * {
 *   <annoDataId> : { tags: {...}, fragment: {...} }
 * }
 * 
 * so we simply replace all fragments with the new fragments from markNodeObj
 */
const handleUpdateFragmentsInAnnoData = (markNodeObj) => {
  setAnnoData(prev => {
    const newAnnoData = prev ? cloneDeep(prev) : {};
    const toReturn = Object.keys(newAnnoData).reduce(
      (a,b) => {
        const thisFragment = markNodeObj[b];
        /**
         * basically if this fragment doesnt exist
         * anymore, simply leave it as it is, and make it as IS_UNUSED
         * 
         * We dont want to delete the annoData because if a redo happens, we
         * should be able to link the fragment with its data via the id
         */
        if(thisFragment){
          const newB = cloneDeep(newAnnoData[b])
          delete newB.IS_UNUSED;
          return ({
            ...a,
            [b]: {
              ...newB,
              fragment: markNodeObj[b]
            }
          })  
        }else{
          return ({
            ...a,
            [b]: {
              ...newAnnoData[b],
              IS_UNUSED: true
            }
          })
        }
        },
      {}
    )
    latestAnnoDataRef.current = toReturn;
    return toReturn;
  })
}


  return (
    <AnnoContextLex.Provider
      value={{
       annoData,
       latestAnnoDataRef,
       isAnnoMutatingRef,
       handleSetAnnoData,
       handleUpdateFragmentsInAnnoData
      }}
    >
      {children}
    </AnnoContextLex.Provider>
  );
};

export const useAnnoContextLex = () => {
  return useContext(AnnoContextLex);
};
